import React, { useCallback, useState } from 'react';
import {
  Icon,
  IconName,
  Text,
  Tooltip,
} from '@usealloy/component-library';
import {
  COLUMN_SPACING,
  NODE_HEIGHT,
  SNIPPET_NODE_HEIGHT,
  NODE_WIDTH,
  ROW_SPACING,
  EXPANDED_SNIPPET_NODE_GUTTER,
  SNIPPET_Y_HOIST,
} from 'flow/service/graph';
import type { SnippetNode as ISnippetNode, WorkflowNodeProps } from 'flow/service/graph';
import BaseNode from '../BaseNode';
import {
  StyledChildBackground,
  StyledLabelContainer,
  StyledLabelText,
  StyledSnippetFocusable,
  StyledSnippetIconContainer,
  StyledTooltipDescription,
  StyledVersionBadge,
  collapsedFocusedStyle,
  collapsedSnippetStyle,
  expandedSnippetStyle,
} from './styles';
import {
  selectActionByPublicId,
  selectNodeState,
  selectSnippetCollapsedState,
  selectContainerRelationship,
  selectSubgraphDimensionsById,
  selectWorkflowProps,
} from 'flow/store/redux/selectors';
import { defaultNodeDimensions } from 'flow/service/graph/layout/nodeMatrix';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { toggleSnippetCollapsibleThunk } from 'flow/store/redux/thunks';

function SnippetNode(props: WorkflowNodeProps<ISnippetNode>) {
  const { id, type } = props;
  const { isCollapsed } = useAppSelector(state => selectSnippetCollapsedState(id)(state));
  const nodeState = useAppSelector(state => selectNodeState(state, id));
  const containerRelationship = useAppSelector(state => selectContainerRelationship(state, id));
  const snippetDimensions = useAppSelector(state => selectSubgraphDimensionsById(id)(state));
  const snippetAction = useAppSelector(selectActionByPublicId(id));
  const workflowProps = useAppSelector(selectWorkflowProps);

  const { nodeHeight, nodeWidth } = snippetDimensions ?? defaultNodeDimensions;
  const dispatch = useAppDispatch();

  const version = snippetAction?.snippet?.publicVersion;
  const snippetToken = snippetAction?.snippet?.token;
  const snippetDescription = snippetToken
    ? workflowProps?.snippets?.[snippetToken]?.description
    : undefined;

  const columnSpacesCount = nodeWidth - 1;
  const expandedWidth = (
    (NODE_WIDTH * nodeWidth)
    + (COLUMN_SPACING * columnSpacesCount)
    + EXPANDED_SNIPPET_NODE_GUTTER
  );
  const rowSpacesCount = nodeHeight - 1;
  const expandedBackgroundHeight = (
    (NODE_HEIGHT * rowSpacesCount)
    + (ROW_SPACING * (rowSpacesCount))
  );
  const width = isCollapsed ? NODE_WIDTH : expandedWidth;
  const labelExpandedMaxWidth = expandedWidth - 130;

  // Todo:
  // - dynamically load in snippet configurations

  const [isHeaderHovered, setIsHeaderHovered] = useState(false);

  const handleIconClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(toggleSnippetCollapsibleThunk(id));
  }, [dispatch, id]);

  const getSnippetIcon = () => {
    if (!isHeaderHovered) return IconName.AddressCard;
    return isCollapsed ? IconName.UpRightDownLeftFromCenter : IconName.DownLeftUpRightToCenter;
  };

  const SnippetIcon = (
    <StyledSnippetIconContainer onClick={handleIconClick} isHovered={isHeaderHovered}>
      <Icon
        icon={getSnippetIcon()}
        size="large"
        color="var(--cl-gray-000)"
      />
    </StyledSnippetIconContainer>
  );

  const labelText = isCollapsed && version != null
    ? `Linked Snippet v${version}`
    : 'Linked Snippet';

  const Label = (
    <StyledLabelContainer $isExpanded={!isCollapsed} $expandedMaxWidth={labelExpandedMaxWidth}>
      <Text size={1} style={{ color: 'var(--cl-text-muted)' }}>
        {labelText}
      </Text>
      <StyledLabelText $isExpanded={!isCollapsed} $expandedMaxWidth={labelExpandedMaxWidth}>
        {snippetAction?.snippet?.name ?? 'Snippet'}
      </StyledLabelText>
    </StyledLabelContainer>
  );

  const VersionBadge = (
    <StyledVersionBadge>
      v{version}
    </StyledVersionBadge>
  );

  const focusedStyle = containerRelationship === 'source'
    ? collapsedFocusedStyle : undefined;

  return (
    <StyledSnippetFocusable
      nodeState={nodeState}
      containerRelationship={containerRelationship}
    >
      <Tooltip
        content={<StyledTooltipDescription>{snippetDescription}</StyledTooltipDescription>}
        hide={!isCollapsed || !snippetDescription}
        side="bottom"
        align="start"
        variant="secondary"
        delayDuration={750}
        disableHoverableContent
        doHideArrow
      >
        <div
          onMouseEnter={() => setIsHeaderHovered(true)}
          onMouseLeave={() => setIsHeaderHovered(false)}
        >
          <BaseNode
            id={id}
            icon={SnippetIcon}
            label={Label}
            style={{
              ...(isCollapsed ? collapsedSnippetStyle : expandedSnippetStyle),
              borderStyle: 'solid',
              borderColor: 'var(--cl-gray-300)',
              padding: 'var(--cl-space-3)',
              width,
              height: SNIPPET_NODE_HEIGHT,
            }}
            stateStyle={{
              baseBackgroundColor: 'var(--cl-gray-000)',
              highlighted: {
                backgroundColor: 'var(--cl-gray-000)',
              },
              ...(isCollapsed && {
                hovered: {
                  backgroundColor: 'var(--cl-gray-100)',
                },
              }),
              focused: focusedStyle,
              'focused-hovered': focusedStyle,
            }}
            type={type}
            disable={{
              nodeMenuHoverState: !isCollapsed,
              tooltip: true,
            }}
            handleStyle={{
              left: {
                top: (NODE_HEIGHT / 2) + (-SNIPPET_Y_HOIST),
                left: isCollapsed ? 1 : (EXPANDED_SNIPPET_NODE_GUTTER / 2) + 1,
              },
              right: { top: (NODE_HEIGHT / 2) + (-SNIPPET_Y_HOIST) },
            }}
          >
            {!isCollapsed ? VersionBadge : undefined}
          </BaseNode>
        </div>
      </Tooltip>
      {
          !isCollapsed && (
            <StyledChildBackground
              height={expandedBackgroundHeight}
              width={width}
            />
          )
        }
    </StyledSnippetFocusable>
  );
}

export default React.memo(SnippetNode);
